import axios from 'axios';

const api = axios.create({
    baseURL: process.env.REACT_APP_PRACTICAS_API_URL || 'https://practicas-services.xn--am-yja.org',
    timeout: 30000,
    headers: { 
        'Content-Type': 'application/json'
    }
});

// Interceptor para agregar API Key y JWT Token a todas las peticiones
api.interceptors.request.use((config) => {
    // Usar la API Key de la variable de entorno o la key por defecto
    const apiKey = process.env.REACT_APP_PRACTICAS_API_KEY || '5fbe752c22e9cdd96e270d02ce532a7d73466e58e9557c173e396dcc0e3c0491';
    
    if (apiKey) {
        config.headers['X-API-Key'] = apiKey;
    } else {
        console.warn('⚠️ REACT_APP_PRACTICAS_API_KEY no está configurada');
    }
    
    // Agregar JWT Token si está disponible (puede ser requerido para algunos endpoints)
    const jwtToken = localStorage.getItem('atfgscomm');
    if (jwtToken) {
        config.headers['Authorization'] = `Bearer ${jwtToken}`;
    }
    
    // Log para debugging (solo en desarrollo)
    if (process.env.NODE_ENV === 'development') {
        console.log('🔑 API Key configurada:', apiKey ? '✅ Sí' : '❌ No');
        console.log('🔐 JWT Token:', jwtToken ? '✅ Sí' : '❌ No');
        console.log('📡 Request URL:', config.baseURL + config.url);
        console.log('📋 Headers:', {
            'X-API-Key': apiKey ? '***' + apiKey.slice(-4) : 'No configurada',
            'Authorization': jwtToken ? 'Bearer ***' : 'No configurado'
        });
    }
    
    return config;
}, (error) => {
    return Promise.reject(error);
});

export default class LiceosAPI {
    // ==================== LICEOS ====================

    static getAll(filters = {}) {
        return new Promise((resolve, reject) => {
            api.get('/api/liceos', { params: filters })
                .then(res => {
                    // Manejar diferentes formatos de respuesta
                    if (res.data.success && res.data.data) {
                        resolve(res.data.data);
                    } else if (res.data.liceos) {
                        resolve(res.data);
                    } else {
                        resolve(res.data);
                    }
                })
                .catch(err => reject(err))
        });
    }

    static getById(id) {
        return new Promise((resolve, reject) => {
            api.get(`/api/liceos/${id}`)
                .then(res => {
                    if (res.data.success && res.data.data) {
                        resolve(res.data.data);
                    } else {
                        resolve(res.data);
                    }
                })
                .catch(err => reject(err))
        });
    }

    static create(data) {
        return new Promise((resolve, reject) => {
            api.post('/api/liceos', data)
                .then(res => {
                    if (res.data.success && res.data.data) {
                        resolve(res.data.data);
                    } else {
                        resolve(res.data);
                    }
                })
                .catch(err => reject(err))
        });
    }

    static update(id, data) {
        return new Promise((resolve, reject) => {
            api.put(`/api/liceos/${id}`, data)
                .then(res => {
                    if (res.data.success && res.data.data) {
                        resolve(res.data.data);
                    } else {
                        resolve(res.data);
                    }
                })
                .catch(err => reject(err))
        });
    }

    static delete(id) {
        return new Promise((resolve, reject) => {
            api.delete(`/api/liceos/${id}`)
                .then(res => resolve(res.data))
                .catch(err => reject(err))
        });
    }

    static getRanking() {
        return new Promise((resolve, reject) => {
            api.get('/api/liceos/ranking')
                .then(res => {
                    if (res.data.success && res.data.data) {
                        resolve(res.data.data);
                    } else {
                        resolve(res.data);
                    }
                })
                .catch(err => reject(err))
        });
    }

    static getStats(id) {
        return new Promise((resolve, reject) => {
            api.get(`/api/liceos/${id}/stats`)
                .then(res => {
                    if (res.data.success && res.data.data) {
                        resolve(res.data.data);
                    } else {
                        resolve(res.data);
                    }
                })
                .catch(err => reject(err))
        });
    }

    static getRankingById(id, filters = {}) {
        return new Promise((resolve, reject) => {
            api.get(`/api/liceos/${id}/ranking`, { params: filters })
                .then(res => {
                    if (res.data.success && res.data.data) {
                        resolve(res.data.data);
                    } else {
                        resolve(res.data);
                    }
                })
                .catch(err => reject(err))
        });
    }

    // ==================== USUARIOS DE LICEOS ====================

    static getLiceoUsers(liceoId, filters = {}) {
        return new Promise((resolve, reject) => {
            api.get(`/api/liceos/${liceoId}/users`, { params: filters })
                .then(res => {
                    if (res.data.success && res.data.data) {
                        resolve(res.data.data);
                    } else if (res.data.users && Array.isArray(res.data.users)) {
                        resolve(res.data);
                    } else if (Array.isArray(res.data)) {
                        resolve({ users: res.data });
                    } else {
                        resolve(res.data);
                    }
                })
                .catch(err => reject(err))
        });
    }

    static addUser(liceoId, userId) {
        return new Promise((resolve, reject) => {
            api.post(`/api/liceos/${liceoId}/users`, { userId })
                .then(res => {
                    if (res.data.success && res.data.data) {
                        resolve(res.data.data);
                    } else {
                        resolve(res.data);
                    }
                })
                .catch(err => reject(err))
        });
    }

    static removeUser(liceoId, userId) {
        return new Promise((resolve, reject) => {
            api.delete(`/api/liceos/${liceoId}/users/${userId}`)
                .then(res => resolve(res.data))
                .catch(err => reject(err))
        });
    }

    // ==================== USUARIOS ====================

    static getUsers(filters = {}) {
        return new Promise((resolve, reject) => {
            api.get('/api/users', { params: filters })
                .then(res => {
                    if (res.data.success && res.data.data) {
                        resolve(res.data.data);
                    } else {
                        resolve(res.data);
                    }
                })
                .catch(err => reject(err))
        });
    }

    static getUserById(id) {
        return new Promise((resolve, reject) => {
            api.get(`/api/users/${id}`)
                .then(res => {
                    if (res.data.success && res.data.data) {
                        resolve(res.data.data);
                    } else {
                        resolve(res.data);
                    }
                })
                .catch(err => reject(err))
        });
    }

    static createUser(data) {
        return new Promise((resolve, reject) => {
            api.post('/api/users', data)
                .then(res => {
                    if (res.data.success && res.data.data) {
                        resolve(res.data.data);
                    } else {
                        resolve(res.data);
                    }
                })
                .catch(err => reject(err))
        });
    }

    static createUserComplete(data) {
        return new Promise((resolve, reject) => {
            api.post('/api/users/create', data)
                .then(res => {
                    if (res.data.success && res.data.data) {
                        resolve(res.data.data);
                    } else {
                        resolve(res.data);
                    }
                })
                .catch(err => reject(err))
        });
    }

    static updateUser(id, data) {
        return new Promise((resolve, reject) => {
            api.put(`/api/users/${id}`, data)
                .then(res => {
                    if (res.data.success && res.data.data) {
                        resolve(res.data.data);
                    } else {
                        resolve(res.data);
                    }
                })
                .catch(err => reject(err))
        });
    }

    static deleteUser(id) {
        return new Promise((resolve, reject) => {
            api.delete(`/api/users/${id}`)
                .then(res => resolve(res.data))
                .catch(err => reject(err))
        });
    }

    static getUserTypes() {
        return new Promise((resolve, reject) => {
            api.get('/api/users/create/types')
                .then(res => {
                    if (res.data.success && res.data.data) {
                        resolve(res.data.data);
                    } else {
                        resolve(res.data);
                    }
                })
                .catch(err => reject(err))
        });
    }

    // ==================== ESTADÍSTICAS ====================

    static getStatisticsOverview(filters = {}) {
        return new Promise((resolve, reject) => {
            api.get('/api/statistics/overview', { params: filters })
                .then(res => {
                    if (res.data.success && res.data.data) {
                        resolve(res.data.data);
                    } else {
                        resolve(res.data);
                    }
                })
                .catch(err => reject(err))
        });
    }

    static getStatisticsApplications(filters = {}) {
        return new Promise((resolve, reject) => {
            api.get('/api/statistics/applications', { params: filters })
                .then(res => {
                    if (res.data.success && res.data.data) {
                        resolve(res.data.data);
                    } else {
                        resolve(res.data);
                    }
                })
                .catch(err => reject(err))
        });
    }

    static getStatisticsPractices(filters = {}) {
        return new Promise((resolve, reject) => {
            api.get('/api/statistics/practices', { params: filters })
                .then(res => {
                    if (res.data.success && res.data.data) {
                        resolve(res.data.data);
                    } else {
                        resolve(res.data);
                    }
                })
                .catch(err => reject(err))
        });
    }

    static getStatisticsUsers(filters = {}) {
        return new Promise((resolve, reject) => {
            api.get('/api/statistics/users', { params: filters })
                .then(res => {
                    if (res.data.success && res.data.data) {
                        resolve(res.data.data);
                    } else {
                        resolve(res.data);
                    }
                })
                .catch(err => reject(err))
        });
    }

    static getKPIs(filters = {}) {
        return new Promise((resolve, reject) => {
            api.get('/api/statistics/kpis', { params: filters })
                .then(res => {
                    if (res.data.success && res.data.data) {
                        resolve(res.data.data);
                    } else {
                        resolve(res.data);
                    }
                })
                .catch(err => reject(err))
        });
    }

    // ==================== DETALLE DE USUARIO ====================

    static getUserDetail(userId) {
        return new Promise((resolve, reject) => {
            api.get(`/api/users/${userId}/detail`)
                .then(res => {
                    if (res.data.success && res.data.data) {
                        resolve(res.data.data);
                    } else {
                        resolve(res.data);
                    }
                })
                .catch(err => reject(err))
        });
    }

    static getUserPoints(userId) {
        return new Promise((resolve, reject) => {
            api.get(`/api/users/${userId}/points`)
                .then(res => {
                    if (res.data.success && res.data.data) {
                        resolve(res.data.data);
                    } else {
                        resolve(res.data);
                    }
                })
                .catch(err => reject(err))
        });
    }

    static getUserLearnwordsCourses(userId) {
        return new Promise((resolve, reject) => {
            api.get(`/api/users/${userId}/learnwords`)
                .then(res => {
                    if (res.data.success && res.data.data) {
                        resolve(res.data.data);
                    } else {
                        resolve(res.data);
                    }
                })
                .catch(err => reject(err))
        });
    }

    // ==================== LEARNWORLDS ====================

    static getLearnworldsUserCoursesByKeycloakId(keycloakUserId, { forceSync = true } = {}) {
        return new Promise((resolve, reject) => {
            api.get(`/api/learnworlds/users/${keycloakUserId}/courses`, {
                params: { forceSync }
            })
                .then(res => {
                    if (res.data.success && res.data.data) {
                        resolve(res.data.data);
                    } else {
                        resolve(res.data);
                    }
                })
                .catch(err => reject(err))
        });
    }

    static getLearnworldsUserCoursesByEmail(email, { forceSync = true } = {}) {
        return new Promise((resolve, reject) => {
            api.get(`/api/learnworlds/users/${encodeURIComponent(email)}/courses`, {
                params: { forceSync }
            })
                .then(res => {
                    if (res.data.success && res.data.data) {
                        resolve(res.data.data);
                    } else {
                        resolve(res.data);
                    }
                })
                .catch(err => reject(err))
        });
    }

    // ==================== EXP POINTS (Puntos de Experiencia) ====================

    static getExpPointsSegments() {
        return new Promise((resolve, reject) => {
            api.get('/api/exp-points/segments')
                .then(res => resolve(res.data))
                .catch(err => reject(err))
        });
    }

    /**
     * Obtiene la billetera completa por ID (Keycloak User ID - UUID)
     * GET /api/exp-points/wallet/:id
     */
    static getExpPointsWalletByUserId(keycloakUserId) {
        return new Promise((resolve, reject) => {
            api.get(`/api/exp-points/wallet/${keycloakUserId}`)
                .then(res => {
                    if (res.data?.success && res.data?.data) {
                        resolve(res.data.data);
                    } else {
                        resolve(res.data);
                    }
                })
                .catch(err => reject(err))
        });
    }

    /**
     * Obtiene balance por segmento por ID (Keycloak User ID - UUID)
     * GET /api/exp-points/segment/:id?segmentId=DOCENTES
     */
    static getExpPointsSegmentByUserId(keycloakUserId, segmentId) {
        return new Promise((resolve, reject) => {
            api.get(`/api/exp-points/segment/${keycloakUserId}`, {
                params: segmentId ? { segmentId } : undefined
            })
                .then(res => {
                    if (res.data?.success && res.data?.data) {
                        resolve(res.data.data);
                    } else {
                        resolve(res.data);
                    }
                })
                .catch(err => reject(err))
        });
    }
}

